// Self-hosted web fonts (previously loaded from Google Fonts).
//
// The @font-face rules below are inlined into the server-rendered <head>
// (src/pages/+Head.tsx), so there's no render-blocking stylesheet and no
// third-party connection: the woff2 files are hashed Vite assets served from
// our own /assets with a 1y immutable cache. It also stops sending every
// visitor's IP to Google.
//
// This module is imported by theme.tsx as well as +Head.tsx, so the client
// build emits the font files - +Head.tsx alone only runs server-side.
//
// Exact parity with what the site got from Google Fonts' legacy css API
// (`css?family=Roboto:300,400,500,600,700` and `css?family=Montserrat:wght@300`):
// - Roboto: weights 300, 400, 500, 600 and 700. Each is declared as its own
//   face pointing at the variable file, which pins the weight axis to that
//   value, so anything heavier (the few `fontWeight: 900` styles) still
//   resolves to 700, exactly as before.
// - Montserrat: weight 400 only. The legacy API doesn't understand `wght@`,
//   so Google ignored the "300" and served 400; buttons ask for 400-600 and
//   have always rendered in that face (600 as synthetic bold).
// - Latin subset only. It covers English, French, Seychellois Creole and
//   Afrikaans, whose accents (é è à ç ê ë ô ï) are all in Latin-1. Rarer
//   letters (e.g. ā č ł ő ş ž) and other scripts render in the metric-matched
//   fallback below. Not Latin Extended too: Vike preloads every font asset in
//   a page's import graph, and a preload downloads the file whether or not
//   unicode-range ever matches - so each extra subset would cost every visitor
//   a download (~30KB per family) for characters the site doesn't use.
//
// Vike's automatic preload also means both files here are fetched early at
// high priority, as soon as the HTML is parsed - no manual preload needed.
//
// "Montserrat Fallback" is Arial with metric overrides computed from
// Montserrat's own metrics, so button text rendered before the web font
// arrives takes up the same space. Roboto deliberately has no equivalent:
// the theme must keep MUI's default Roboto font stack (see theme.tsx), and
// Roboto is preloaded anyway.
import montserratLatin from '@fontsource/montserrat/files/montserrat-latin-400-normal.woff2?url';
import robotoLatin from '@fontsource-variable/roboto/files/roboto-latin-wght-normal.woff2?url';

export const MONTSERRAT_STACK =
  '"Montserrat", "Montserrat Fallback", "Helvetica", "Arial", sans-serif';

const LATIN =
  'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';

const face = (
  family: string,
  weight: string,
  url: string,
  unicodeRange: string
) =>
  `@font-face{font-family:'${family}';font-style:normal;font-display:swap;font-weight:${weight};src:url(${url}) format('woff2');unicode-range:${unicodeRange}}`;

const fallback = (
  family: string,
  sizeAdjust: string,
  ascent: string,
  descent: string
) =>
  `@font-face{font-family:'${family}';src:local('Arial');size-adjust:${sizeAdjust};ascent-override:${ascent};descent-override:${descent};line-gap-override:0%}`;

export const FONT_FACE_CSS = [
  ...['300', '400', '500', '600', '700'].map((weight) =>
    face('Roboto', weight, robotoLatin, LATIN)
  ),
  face('Montserrat', '400', montserratLatin, LATIN),
  fallback('Montserrat Fallback', '114.29%', '84.69%', '21.96%'),
].join('');
